import React, { useEffect, useMemo, useRef, useState } from 'react';

import EmptyState from '~/components/core/EmptyState';
import { useCms } from '~/components/hooks/useCms';
import { EmptyReportsIllustration } from '~/components/illustrations';
import LoadingIndicator from '~/components/LoadingIndicator';
import { signalUserPresence } from '~/components/SessionTimeoutWarning/userPresence';
import useDataFetcher from '~/components/useDataFetcher';
import type { MetabaseEmbeddingJwtResponse } from '~/server_shared/openapi/data-contracts';

// The page mounts under either /reports (METABASE_APPROVED on) or /reports_v3 (legacy).
// We lock onto whichever base the user landed on so route changes don't reload the iframe.
const REPORTS_BASE_DEFAULT = '/reports';
const REPORTS_BASE_LEGACY = '/reports_v3';
const NEW_BUTTON_PARAM = 'new_button';
const NEW_BUTTON_VALUE = 'true';

const detectReportsBase = (): string =>
  window.location.pathname === REPORTS_BASE_LEGACY || window.location.pathname.startsWith(`${REPORTS_BASE_LEGACY}/`)
    ? REPORTS_BASE_LEGACY
    : REPORTS_BASE_DEFAULT;

const SPLIT_PATH_DUMMY_BASE = 'http://dummy.invalid';

const splitPath = (path: string): { pathname: string; searchPart: string; hashPart: string } => {
  const url = new URL(path, SPLIT_PATH_DUMMY_BASE);
  return {
    pathname: url.pathname || '/',
    searchPart: url.search.slice(1),
    hashPart: url.hash.slice(1),
  };
};

const joinPath = (pathname: string, searchPart: string, hashPart: string): string => {
  const search = searchPart ? `?${searchPart}` : '';
  const hash = hashPart ? `#${hashPart}` : '';
  return `${pathname}${search}${hash}`;
};

// `new_button=true` is a Metabase-side flag that controls the "+ New" button visibility.
// We always inject it into outbound iframe URLs and strip it from the displayed parent URL.
const ensureNewButton = (path: string): string => {
  const { pathname, searchPart, hashPart } = splitPath(path);
  const params = new URLSearchParams(searchPart);
  params.set(NEW_BUTTON_PARAM, NEW_BUTTON_VALUE);
  return joinPath(pathname, params.toString(), hashPart);
};

const stripNewButton = (path: string): string => {
  const { pathname, searchPart, hashPart } = splitPath(path);
  if (!searchPart) return path;
  const params = new URLSearchParams(searchPart);
  params.delete(NEW_BUTTON_PARAM);
  return joinPath(pathname, params.toString(), hashPart);
};

type MetabaseLocation = string | { pathname?: string; search?: string; hash?: string };

interface MetabaseLocationMessage {
  metabase: { type: 'location'; location: MetabaseLocation };
}

const isMetabaseLocationMessage = (data: unknown): data is MetabaseLocationMessage => {
  if (!data || typeof data !== 'object') return false;
  const wrapped = (data as { metabase?: unknown }).metabase;
  if (!wrapped || typeof wrapped !== 'object') return false;
  const inner = wrapped as { type?: unknown; location?: unknown };
  if (inner.type !== 'location') return false;
  if (typeof inner.location === 'string') return true;
  return typeof inner.location === 'object' && inner.location !== null;
};

const extractPath = (location: MetabaseLocation, origin: string): string => {
  if (typeof location === 'string') {
    try {
      const url = new URL(location, origin);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return location.startsWith('/') ? location : `/${location}`;
    }
  }
  return `${location.pathname || '/'}${location.search || ''}${location.hash || ''}`;
};

// Map between our browser URL (<base><rest>) and the Metabase iframe path (<rest>).
// Anything after <base> in pathname/search/hash belongs to Metabase.
const readMetabasePathFromUrl = (base: string): string => {
  const { pathname, search, hash } = window.location;
  let rest = '/';
  if (pathname.startsWith(`${base}/`)) {
    rest = pathname.slice(base.length);
  }
  return `${rest}${search}${hash}`;
};

const writeMetabasePathToUrl = (base: string, metabasePath: string): void => {
  const trimmed = metabasePath.startsWith('/') ? metabasePath : `/${metabasePath}`;
  // Drop the trailing "/" for the home path so we land on <base>, not <base>/.
  const newUrl = trimmed === '/' ? base : `${base}${trimmed}`;
  window.history.replaceState(null, '', newUrl);
};

export const ReportingV3Page: React.FC = () => {
  const { setPageTitle, userOrganization } = useCms();
  const orgId = userOrganization?.id;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Lock the base path at mount: route changes between /reports and /reports_v3 would
  // otherwise change iframe src and drop the Metabase session.
  const [reportsBase] = useState<string>(() => detectReportsBase());
  // Snapshot the deep-link target on mount so iframe src never recomputes
  // (changing src reloads the iframe and drops the Metabase session).
  const [initialMetabasePath] = useState<string>(() => readMetabasePathFromUrl(reportsBase));

  useEffect(() => {
    setPageTitle('Reports');
  }, [setPageTitle]);

  const {
    isLoading,
    isError,
    data: jwtData,
  } = useDataFetcher<MetabaseEmbeddingJwtResponse>(`/api/v1/metabase/organizations/${orgId}/embedding_jwt`, null, {
    shouldFetch: !!orgId,
  });

  const metabaseOrigin = useMemo(() => {
    if (!jwtData) return null;
    try {
      return new URL(jwtData.metabase_site_url).origin;
    } catch {
      return null;
    }
  }, [jwtData]);

  const iframeSrc = useMemo(() => {
    if (!jwtData) return null;
    const returnPath = ensureNewButton(initialMetabasePath);
    return `${jwtData.metabase_site_url}/auth/sso?jwt=${jwtData.jwt}&return_to=${encodeURIComponent(returnPath)}`;
  }, [jwtData, initialMetabasePath]);

  // iframe → parent: mirror Metabase navigation into /reports<path>.
  useEffect(() => {
    if (!metabaseOrigin) return undefined;
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== metabaseOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!isMetabaseLocationMessage(event.data)) return;
      const newPath = stripNewButton(extractPath(event.data.metabase.location, metabaseOrigin));
      if (readMetabasePathFromUrl(reportsBase) === newPath) return;
      writeMetabasePathToUrl(reportsBase, newPath);
      signalUserPresence();
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [metabaseOrigin, reportsBase]);

  // parent → iframe: forward browser back/forward into Metabase.
  useEffect(() => {
    if (!metabaseOrigin) return undefined;
    const handlePopState = () => {
      const { pathname } = window.location;
      if (pathname !== reportsBase && !pathname.startsWith(`${reportsBase}/`)) return;
      const path = ensureNewButton(readMetabasePathFromUrl(reportsBase));
      iframeRef.current?.contentWindow?.postMessage({ metabase: { type: 'location', location: path } }, metabaseOrigin);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [metabaseOrigin, reportsBase]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  if (isError || !iframeSrc) {
    return (
      <EmptyState
        title="Unable to load Reports"
        subtitle="Metabase is not configured for this organization."
        illustration={<EmptyReportsIllustration />}
        className="flex h-full flex-col items-center justify-center"
      />
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      <iframe
        ref={iframeRef}
        title="Metabase Reports"
        src={iframeSrc}
        className="h-full w-full border-0"
        allow="fullscreen"
        referrerPolicy="no-referrer"
      />
    </div>
  );
};
